import { Container, Graphics } from 'pixi.js';
import { Symbol } from './Symbol.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Cumulative extra strip cells for column index `col` so total stagger travel strictly
 * increases with each reel (avoids integer-round collisions when ideal cells per step is below 1).
 * @param {number} col 0-based column (reel 0 → 0)
 * @param {number} deltaIdeal cells per stagger step ≈ v×stagger/(rCruise×cellHeight)
 */
function cumulativeStaggerTravelCells(col, deltaIdeal) {
  if (col <= 0 || !(deltaIdeal > 0)) return 0;
  let prev = 0;
  for (let c = 1; c <= col; c++) {
    let t = Math.round(c * deltaIdeal);
    if (t <= prev) t = prev + 1;
    prev = t;
  }
  return prev;
}

export class Reel {
  /**
   * @param {{
   *   index: number,
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.index = opts.index;
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;

    this.root = new Container();
    this.maskShape = new Graphics();

    /** @type {Container} */
    this.stripContainer = new Container();
    this.root.addChild(this.stripContainer);
    this._applyMask();

    /** @type {number[]} */
    this._stripData = [];
    /** @type {Symbol[]} */
    this._stripSymbols = [];

    this._ensureStripPool(this._getRequiredStripLength());
    this._landIndex = this._getDefaultLandIndex();
    this._syncAllSymbolsFromData();
    this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + gameConfig.rowCount + 1);
    this._applyStripScrollPosition();
    /** @type {import('gsap').core.Timeline | null} */
    this._spinTimeline = null;
    /** @type {import('gsap').core.Tween | null} */
    this._pendingSpinTween = null;
    /** @type {import('gsap').core.Tween | null} */
    this._fastStopTween = null;
    /** Last strip index range kept active during spin (narrow viewport window); null when not using sliding activation. */
    this._spinActiveWindow = null;
    /** True while cruise-phase Spine blur is active (cleared at `land`, before deceleration). */
    this._scrollBlurActive = false;
    /** Inclusive strip index range receiving blur during the current spin. */
    this._scrollBlurLo = 0;
    this._scrollBlurHi = -1;
  }

  _getSpinDirection() {
    return (gameConfig.reelSpinDirection ?? 1) < 0 ? -1 : 1;
  }

  _getMaxTravelCells() {
    const maxLanding =
      gameConfig.stripLandingOffsetMin + Math.max(0, gameConfig.stripLandingOffsetSpread - 1);
    const maxExtra =
      gameConfig.extraSpinCellsMin + Math.max(0, gameConfig.extraSpinCellsSpread - 1);
    const base = maxLanding + maxExtra;
    const staggerExtra = this._getMaxStaggerExtraCells();
    return base + staggerExtra;
  }

  /**
   * Worst-case extra strip cells for right-most reel (stagger at constant cruise speed).
   */
  _getMaxStaggerExtraCells() {
    const ch = gameConfig.cellHeight;
    const ratios = gameConfig.spinDistanceRatios;
    const rCruise = ratios?.cruise ?? 0.7;
    if (rCruise <= 0 || ch <= 0) return 0;
    const lastCol = Math.max(0, gameConfig.reelCount - 1);
    const stagger = gameConfig.reelStopStaggerSec ?? 0;
    if (lastCol === 0 || stagger <= 0) return 0;
    const v = this._resolveCruisePxPerSec();
    const deltaIdeal = (v * stagger) / (rCruise * ch);
    return cumulativeStaggerTravelCells(lastCol, deltaIdeal) + 4;
  }

  /**
   * Shared cruise speed (px/s) — same for every reel and bet tier; only stagger spacing may differ.
   */
  _resolveCruisePxPerSec() {
    const ch = gameConfig.cellHeight;
    const ratios = gameConfig.spinDistanceRatios;
    const rCruise = ratios?.cruise ?? 0.7;
    const tCruise = Math.max(0.05, gameConfig.spinPhaseCruise || 0.26);
    const refCells =
      gameConfig.stripLandingOffsetMin +
      gameConfig.extraSpinCellsMin +
      Math.floor(
        (Math.max(0, gameConfig.stripLandingOffsetSpread - 1) +
          Math.max(0, gameConfig.extraSpinCellsSpread - 1)) /
          2,
      );
    const refCruisePx = refCells * ch * rCruise;
    const derived = refCruisePx / tCruise;
    const cap = Math.max(500, Number(gameConfig.reelCruiseDerivedCapPxPerSec) || 3400);
    const configured = gameConfig.reelCruisePixelsPerSecond;
    return configured != null && Number.isFinite(configured) && configured > 0
      ? configured
      : Math.min(derived, cap);
  }

  _getRequiredStripLength() {
    const baseForDownward = this._getMaxTravelCells() + 20 + gameConfig.rowCount;
    return Math.max(gameConfig.stripMinLength, baseForDownward + 8);
  }

  _getDefaultLandIndex() {
    if (this._getSpinDirection() > 0) return 10;
    return this._getMaxTravelCells() + 14;
  }

  _applyMask() {
    // Masking disabled: allow Spine animations to render fully without clipping.
    this.stripContainer.mask = null;
  }

  _ensureStripPool(length) {
    const { cellWidth: cw, cellHeight: ch } = gameConfig;
    while (this._stripData.length < length) {
      const i = this._stripData.length;
      this._stripData.push(this._getRandomSymbolId());
      const sym = new Symbol({
        spineAssets: this.spineAssets,
        animationController: this.animationController,
      });
      sym.setSymbolId(this._stripData[i]);
      sym.root.x = cw / 2;
      sym.root.y = i * ch + ch / 2;
      this.stripContainer.addChild(sym.root);
      this._stripSymbols.push(sym);
    }
    if (this._stripData.length > length) {
      for (let j = length; j < this._stripSymbols.length; j++) {
        const sym = this._stripSymbols[j];
        if (sym.root.parent === this.stripContainer) {
          this.stripContainer.removeChild(sym.root);
        }
        sym.destroy();
      }
      this._stripSymbols.length = length;
      this._stripData.length = length;
    }
  }

  /**
   * Randomize strip cells outside the landing window (filler while spinning).
   * @param {number} landIndex First index of the result window (protected).
   * @param {number} rowCount Rows in the result window.
   */
  _randomizeStripOutsideWindow(landIndex, rowCount) {
    for (let i = 0; i < this._stripData.length; i++) {
      if (i >= landIndex && i < landIndex + rowCount) continue;
      this._stripData[i] = this._getRandomSymbolId();
    }
  }

  _getRandomSymbolId() {
    const symbols = gameConfig.symbols;
    if (!Array.isArray(symbols) || symbols.length === 0) return 0;
    const idx = Math.floor(Math.random() * symbols.length);
    return symbols[idx]?.id ?? 0;
  }

  _syncAllSymbolsFromData() {
    for (let i = 0; i < this._stripSymbols.length; i++) {
      this._stripSymbols[i].setSymbolId(this._stripData[i]);
    }
  }

  _setActiveSymbolRange(fromIndex, toIndex) {
    const lo = Math.max(0, fromIndex);
    const hi = Math.min(this._stripSymbols.length - 1, toIndex);
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const shouldBeActive = i >= lo && i <= hi;
      if (shouldBeActive) {
        // Unconditionally clear any spin state before activating — blur must never persist.
        this._stripSymbols[i].clearSpinPresentation();
      }
      this._stripSymbols[i].setActive(shouldBeActive);
    }
    this._spinActiveWindow = null;
  }

  _resetSpinActiveWindowState() {
    this._spinActiveWindow = null;
  }

  /**
   * Activates only strip cells near the viewport for the current `stripContainer.y`.
   * Keeps the same scroll motion while avoiding dozens of simultaneous Spine updates off-screen.
   * @param {number} ch cell height
   * @param {number} rowCount visible rows
   * @param {number} pad extra strip indices above/below the window
   */
  _updateStripActiveFromScroll(ch, rowCount, pad) {
    const n = this._stripSymbols.length;
    if (n <= 0 || ch <= 0) return;

    const vy = -this.stripContainer.y / ch;
    const lo = Math.floor(vy) - pad;
    const hi = Math.ceil(vy + rowCount) - 1 + pad;
    const loC = Math.max(0, lo);
    const hiC = Math.min(n - 1, hi);

    const prev = this._spinActiveWindow;
    if (prev && prev.lo === loC && prev.hi === hiC) return;

    if (!prev) {
      for (let i = 0; i < n; i++) {
        const on = i >= loC && i <= hiC;
        this._stripSymbols[i].setSymbolId(this._stripData[i]);
        this._stripSymbols[i].setActive(on);
      }
      this._spinActiveWindow = { lo: loC, hi: hiC };
      this._syncScrollBlurForActiveWindow(loC, hiC);
      return;
    }

    for (let i = prev.lo; i <= prev.hi; i++) {
      if (i < loC || i > hiC) {
        this._stripSymbols[i].setActive(false);
      }
    }
    for (let i = loC; i <= hiC; i++) {
      if (i < prev.lo || i > prev.hi) {
        this._stripSymbols[i].setSymbolId(this._stripData[i]);
        this._stripSymbols[i].setActive(true);
      }
    }
    this._spinActiveWindow = { lo: loC, hi: hiC };
    this._syncScrollBlurForActiveWindow(loC, hiC);
  }

  /** Apply _stripData to sprites in a range except a window. */
  _syncStripSymbolsInRange(skipFrom, skipRowCount, fromIndex = 0, toIndex = this._stripSymbols.length - 1) {
    const lo = Math.max(0, fromIndex);
    const hi = Math.min(this._stripSymbols.length - 1, toIndex);
    for (let i = lo; i <= hi; i++) {
      if (i >= skipFrom && i < skipFrom + skipRowCount) continue;
      this._stripSymbols[i].setSymbolId(this._stripData[i]);
    }
  }

  _applyStripScrollPosition() {
    const ch = gameConfig.cellHeight;
    this.stripContainer.y = -this._landIndex * ch;
  }

  /**
   * Blur / spin clip on every strip cell in [startIdx, endIdx] (inclusive) that can appear
   * in the reel window while the strip scrolls from startIdx to the landing window.
   */
  _setStripIndexRangeSpinning(startIdx, endIdx, spinning) {
    const n = this._stripSymbols.length;
    const lo = Math.max(0, Math.min(startIdx, endIdx));
    const hi = Math.min(n - 1, Math.max(startIdx, endIdx));
    for (let i = lo; i <= hi; i++) {
      this._stripSymbols[i].setSpinning(spinning);
    }
  }

  /** Clear spinning presentation on the whole strip (after a spin or cancel). */
  _setAllStripSymbolsSpinning(spinning) {
    for (let i = 0; i < this._stripSymbols.length; i++) {
      if (spinning) {
        this._stripSymbols[i].setSpinning(true);
      } else {
        this._stripSymbols[i].clearSpinPresentation();
      }
    }
  }

  _setVisibleWindowSpinning(spinning) {
    this._setStripIndexRangeSpinning(this._landIndex, this._landIndex + gameConfig.rowCount - 1, spinning);
  }

  _enableScrollBlur(startIdx, endIdx) {
    const lo = Math.max(0, Math.min(startIdx, endIdx));
    const hi = Math.max(startIdx, endIdx);
    this._scrollBlurActive = true;
    this._scrollBlurLo = lo;
    this._scrollBlurHi = hi;
    // Only apply blur to the currently-active viewport window, not the whole strip.
    // Inactive symbols must never carry _isSpinning=true — they'd re-show blur on activation.
    if (this._spinActiveWindow) {
      this._syncScrollBlurForActiveWindow(this._spinActiveWindow.lo, this._spinActiveWindow.hi);
    }
  }

  _clearScrollBlur() {
    this._scrollBlurActive = false;
    this._scrollBlurLo = 0;
    this._scrollBlurHi = -1;
    // Clear the full strip — catches any active symbol regardless of window position.
    this._setAllStripSymbolsSpinning(false);
  }

  /**
   * Called after every viewport shift:
   *   • blur ON  → apply spinning clip to active cells inside the blur range
   *   • blur OFF → only clear cells that are still marked spinning (avoid restarting idle every frame)
   */
  _syncScrollBlurForActiveWindow(loC, hiC) {
    if (this._scrollBlurActive && this._scrollBlurHi >= this._scrollBlurLo) {
      for (let i = loC; i <= hiC; i++) {
        const sym = this._stripSymbols[i];
        if (i >= this._scrollBlurLo && i <= this._scrollBlurHi) {
          sym.setSpinning(true);
        } else if (sym.isSpinning()) {
          sym.clearSpinPresentation();
        }
      }
      return;
    }
    // Blur is off — only touch cells that are still flagged as spinning.
    for (let i = loC; i <= hiC; i++) {
      if (this._stripSymbols[i].isSpinning()) {
        this._stripSymbols[i].clearSpinPresentation();
      }
    }
  }

  _stopPendingSpin() {
    if (this._pendingSpinTween) {
      const tw = this._pendingSpinTween;
      this._pendingSpinTween = null;
      tw.kill();
      this.animationController.releaseTween(tw);
    }
    this._applyStripScrollPosition();
    this._setVisibleWindowSpinning(false);
  }

  _clearFastStopTween() {
    if (!this._fastStopTween) return;
    const tw = this._fastStopTween;
    this._fastStopTween = null;
    tw.kill();
    this.animationController.releaseTween(tw);
  }

  requestStop() {
    if (!this._spinTimeline) return;
    this._clearFastStopTween();
    const tl = this._spinTimeline;
    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const scrollPad = Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 4);
    /** Turbo: skip cruise only — same landing tweens as tier 1/2 (no speed changes). */
    const tLand = tl.labels?.land;
    if (typeof tLand === 'number' && tl.duration() > 0) {
      if (tl.time() + 1e-6 < tLand) tl.time(tLand);
      this._clearScrollBlur();
      this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
      return;
    }
    tl.totalProgress(1);
  }

  beginPendingSpin() {
    // Keep pre-spin phase visually neutral: no blur until actual reel motion starts.
  }

  /**
   * After a spin, fold the visible triple back to a small land index to avoid drift.
   * @param {number[]} visibleTriple length rowCount (backend result for this reel)
   * @param {number | null} landedAtStripIndex strip index that was in the viewport when the spin
   *   tween finished (before scroll snap). If set, those cells keep `visibleTriple` in `_stripData`
   *   until after sync so we never paint random filler on the symbols the player still sees.
   */
  _normalizeStrip(visibleTriple, landedAtStripIndex = null) {
    const L = this._getRequiredStripLength();
    const base = this._getDefaultLandIndex();
    const rowCount = gameConfig.rowCount;
    this._ensureStripPool(L);
    for (let r = 0; r < rowCount; r++) {
      this._stripData[base + r] = visibleTriple[r];
    }
    if (
      landedAtStripIndex != null &&
      Number.isFinite(landedAtStripIndex) &&
      landedAtStripIndex >= 0 &&
      landedAtStripIndex + rowCount <= this._stripData.length
    ) {
      for (let r = 0; r < rowCount; r++) {
        this._stripData[landedAtStripIndex + r] = visibleTriple[r];
      }
    }
    this._landIndex = base;
    this._syncAllSymbolsFromData();
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const ch = gameConfig.cellHeight;
      this._stripSymbols[i].root.y = i * ch + ch / 2;
    }
    this._applyStripScrollPosition();
  }

  /**
   * @param {number[]} colResult length rowCount top→bottom symbol ids
   * @param {{ columnIndex?: number, fastStop?: boolean, reelStopStaggerScale?: number, travelBase?: { landingOffset: number, extraCells: number } }} [options]
   *   column 0 = left; stagger adds extra strip travel so each column stops later — same px/s for all.
   *   Pass the same `travelBase` from {@link ReelContainer#spinToGrid} for every reel so random travel matches.
   *   `reelStopStaggerScale` tightens stop spacing only (e.g. tier 2). `fastStop` snaps (turbo).
   * @returns {Promise<void>}
   */
  spinToResult(colResult, options = {}) {
    const ch = gameConfig.cellHeight;
    const rowCount = gameConfig.rowCount;
    const col = Math.max(0, Number(options.columnIndex) || 0);
    const staggerScaleRaw = options.reelStopStaggerScale;
    const staggerScale =
      staggerScaleRaw != null && Number.isFinite(staggerScaleRaw) && staggerScaleRaw >= 0
        ? staggerScaleRaw
        : 1;
    const staggerSec = (gameConfig.reelStopStaggerSec ?? 0) * staggerScale;
    const cruisePxPerSec = this._resolveCruisePxPerSec();

    this._clearFastStopTween();
    this._stopPendingSpin();
    this._resetSpinActiveWindowState();
    this._clearScrollBlur();
    if (this._spinTimeline) {
      const prevTl = this._spinTimeline;
      this._spinTimeline = null;
      prevTl.kill();
      this.animationController.releaseTween(prevTl);
    }
    this._applyStripScrollPosition();
    const startIndex = this._landIndex;

    /** What the player actually sees (ground truth — may differ from _stripData if ever out of sync). */
    const visibleSnap = [];
    for (let r = 0; r < rowCount; r++) {
      visibleSnap.push(this._stripSymbols[startIndex + r].getSymbolId());
    }

    const tb = options.travelBase;
    let landingOffset;
    let extraCells;
    if (
      tb &&
      Number.isFinite(tb.landingOffset) &&
      Number.isFinite(tb.extraCells)
    ) {
      landingOffset = tb.landingOffset;
      extraCells = tb.extraCells;
    } else {
      landingOffset =
        gameConfig.stripLandingOffsetMin +
        Math.floor(Math.random() * gameConfig.stripLandingOffsetSpread);
      extraCells =
        gameConfig.extraSpinCellsMin + Math.floor(Math.random() * gameConfig.extraSpinCellsSpread);
    }
    let travel = landingOffset + extraCells;
    const ratiosEarly = gameConfig.spinDistanceRatios;
    const rCruiseEarly = ratiosEarly?.cruise ?? 0.7;
    if (!options.fastStop && staggerSec > 0 && col > 0 && rCruiseEarly > 0) {
      const deltaIdeal = (cruisePxPerSec * staggerSec) / (rCruiseEarly * ch);
      travel += cumulativeStaggerTravelCells(col, deltaIdeal);
    }
    const dir = this._getSpinDirection();
    let stopIndex = startIndex + dir * travel;
    if (stopIndex < 0) stopIndex = 0;

    if (stopIndex + rowCount >= this._stripData.length) {
      this._ensureStripPool(stopIndex + rowCount + 8);
    }

    for (let r = 0; r < rowCount; r++) {
      this._stripData[stopIndex + r] = colResult[r];
    }
    for (let r = 0; r < rowCount; r++) {
      this._stripData[startIndex + r] = visibleSnap[r];
    }
    this._syncStripSymbolsInRange(startIndex, rowCount, stopIndex, stopIndex + rowCount - 1);

    const startY = -startIndex * ch;
    const endY = -stopIndex * ch;
    this.stripContainer.y = startY;

    const scrollPad = Math.max(2, Number(gameConfig.reelSpinViewportPadCells) || 4);
    this._updateStripActiveFromScroll(ch, rowCount, scrollPad);

    const ratios = gameConfig.spinDistanceRatios;
    const total = Math.abs(endY - startY);
    const d1 = total * ratios.accelerate;
    const d2 = total * ratios.cruise;
    const d3 = total * ratios.decelerate;
    const yDir = Math.sign(endY - startY) || -1;
    const y1 = startY + yDir * d1;
    const y2 = y1 + yDir * d2;

    const t1 = gameConfig.spinPhaseAccelerate;
    const t2 = gameConfig.spinPhaseCruise;
    const t3 = gameConfig.spinPhaseDecelerate;

    return new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        const tl = this._spinTimeline;
        this._spinTimeline = null;
        if (tl) this.animationController.releaseTween(tl);
        this._clearFastStopTween();
        // Fold the strip back to a fixed land index so the pool does not grow every spin
        // (each extra cell = another Spine on the shared ticker → severe lag after a few plays).
        this._clearScrollBlur();
        this._normalizeStrip(colResult, stopIndex);
        this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + rowCount + 1);
        // Hard guarantee: after settlement every symbol in the strip must be idle.
        // Iterate the full strip one final time and force-idle anything still spinning.
        for (const sym of this._stripSymbols) {
          sym.clearSpinPresentation();
        }

        resolve();
      };

      this._spinTimeline = this.animationController.createTimeline({
        onComplete: settle,
        onInterrupt: settle,
      });
      if (options.fastStop) {
        this._spinTimeline.timeScale(38);
      }

      const s1 = t1;
      const s3 = t3;
      const cruiseDist = Math.abs(y2 - y1);
      const cruiseDur =
        cruisePxPerSec > 1 && cruiseDist > 0 ? cruiseDist / cruisePxPerSec : t2;

      const bounceRatio = Math.max(0, Number(gameConfig.reelLandBounceRatio) || 0);
      const bounceOutSec = Math.max(0, Number(gameConfig.reelLandBounceOut) || 0);
      const bounceInSec = Math.max(0, Number(gameConfig.reelLandBounceIn) || 0);
      const useLandBounce =
        !options.fastStop && bounceRatio > 0 && bounceOutSec > 0 && s3 > 0.012;
      const bouncePx = bounceRatio * ch;
      /** Past the lock position in the direction of travel, then snap back (classic slot landing). */
      const overshootY = endY + yDir * bouncePx;
      let settleDur = 0;
      let approachDur = s3;
      if (useLandBounce) {
        settleDur = Math.min(bounceOutSec, s3 * 0.5);
        approachDur = s3 - settleDur;
        const minApproach = bounceInSec > 0 ? bounceInSec : 0;
        if (minApproach > 0 && approachDur < minApproach) {
          approachDur = Math.min(minApproach, s3 * 0.88);
          settleDur = Math.max(0.02, s3 - approachDur);
        }
      }

      this._spinTimeline.call(
        () => {
          const blurLo = Math.min(startIndex, stopIndex);
          const blurHi = Math.max(startIndex, stopIndex) + rowCount - 1;
          this._enableScrollBlur(blurLo, blurHi);
        },
        null,
        0,
      );
      const onStripY = () => {
        this._updateStripActiveFromScroll(ch, rowCount, scrollPad);
      };
      if (s1 > 0.001) {
        this._spinTimeline.to(this.stripContainer, {
          y: y1,
          duration: s1,
          ease: 'power2.in',
          onUpdate: onStripY,
        });
      }
      this._spinTimeline.to(this.stripContainer, {
        y: y2,
        duration: cruiseDur,
        ease: 'none',
        onUpdate: onStripY,
      });
      this._spinTimeline.addLabel('land');
      // End of cruise — clear blur before deceleration so symbols are sharp while slowing.
      this._spinTimeline.call(() => this._clearScrollBlur(), null, 'land');

      if (useLandBounce) {
        this._spinTimeline
          .to(this.stripContainer, {
            y: overshootY,
            duration: approachDur,
            ease: 'power3.out',
            onUpdate: onStripY,
          })
          .to(this.stripContainer, {
            y: endY,
            duration: settleDur,
            ease: 'power2.out',
            onUpdate: onStripY,
          });
      } else {
        this._spinTimeline.to(this.stripContainer, {
          y: endY,
          duration: s3,
          ease: 'power3.out',
          onUpdate: onStripY,
        });
      }
    });
  }

  /**
   * Apply result symbols without spin animation.
   * @param {number[]} colResult length rowCount top→bottom symbol ids
   */
  setResultInstant(colResult) {
    this._stopPendingSpin();
    if (this._spinTimeline) {
      const prevTl = this._spinTimeline;
      this._spinTimeline = null;
      prevTl.kill();
      this.animationController.releaseTween(prevTl);
    }
    this._clearScrollBlur();
    this._normalizeStrip(colResult);
    this._setActiveSymbolRange(this._landIndex - 1, this._landIndex + gameConfig.rowCount + 1);
  }

  /**
   * @param {number} row 0 top .. rowCount-1 bottom
   * @returns {Symbol}
   */
  getSymbolAtVisibleRow(row) {
    return this._stripSymbols[this._landIndex + row];
  }

  /**
   * Replace one currently visible symbol (0=top..2=bottom) with backend symbol id.
   * @param {number} row
   * @param {number} symbolId
   */
  setVisibleRowSymbolId(row, symbolId) {
    const sym = this.getSymbolAtVisibleRow(row);
    if (!sym) return;
    sym.setSymbolId(symbolId);
  }

  /**
   * Reset win/spin presentation: always idle the visible window (+ pad), flags-only elsewhere.
   * @see {Symbol#prepareForNewRound}
   */
  resetForNewRound() {
    this._clearScrollBlur();
    const lo = Math.max(0, this._landIndex - 1);
    const hi = Math.min(
      this._stripSymbols.length - 1,
      this._landIndex + gameConfig.rowCount + 1,
    );
    for (let i = 0; i < this._stripSymbols.length; i++) {
      const sym = this._stripSymbols[i];
      const inVisiblePad = i >= lo && i <= hi;
      sym.clearWinArtifacts();
      if (inVisiblePad) {
        sym.setActive(true);
        sym.prepareForNewRound(true);
      }
    }
    this._spinActiveWindow = null;
  }

  /** @deprecated Use {@link #resetForNewRound} */
  resetAllSymbolVisuals() {
    this.resetForNewRound();
  }

  getLandIndex() {
    return this._landIndex;
  }

  getReelIndex() {
    return this.index;
  }
}
